import { useContext, useEffect, useState } from 'react';
import { Button, Dropdown, DropdownButton, Modal } from 'react-bootstrap';
import { FormModal } from '../../global/FormModal';
import { PrimaryButton } from 'components/Buttons';
import { Stars } from 'react-bootstrap-icons';
import Spinner from '../../global/Spinner';
import { useCurrentOrganisation } from 'hooks/useCurrentOrganisation';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AreaContext } from 'pages/documents_v2/views/edit_area/context/AreaContext';
import { getEnv } from 'config/Env';
import { streamEvents } from 'features/streamingApi';
import rehypeRaw from 'rehype-raw';
import Markdown from 'react-markdown';
import { useDispatch } from 'react-redux';
import { authApi } from 'features/security/authApi';
import { TextDiffRender } from 'pages/documents_v2/views/edit_area/TextDiffRender';
import { Switch } from 'components/Forms/Switch';
import { useLocalStorage } from 'hooks/useLocalStorage';

export function BlockImprovementModal({ block, document, handleClose, handleBlockChange }) {
    const currentOrganisation = useCurrentOrganisation();
    const { t } = useTranslation('documents');
    const [suggestion, setSuggestion] = useState('');

    const hasRateLimit = currentOrganisation && currentOrganisation.aiRateLimit >= 0;
    const rateLimitExceeded = hasRateLimit && currentOrganisation.aiUsageCount >= currentOrganisation.aiRateLimit;

    return (
        <FormModal
            show={true}
            onHide={handleClose}
            title={
                <>
                    <Stars className="text-purple mr-2" />
                    {t('documents:document.navbar.main.editor.left.blocks.modals.blockImprovementModal.title')}
                </>
            }
            size="lg"
        >
            <>
                {rateLimitExceeded ? (
                    <RateLimitExceededMessage handleClose={handleClose} />
                ) : (
                    <>
                        <RenderedBody
                            block={block}
                            document={document}
                            suggestion={suggestion}
                            setSuggestion={setSuggestion}
                        />

                        <Modal.Footer>
                            <Button variant="btn btn-secondary" onClick={() => handleClose()}>
                                {t('btn.cancel')}
                            </Button>
                            <Button
                                variant="btn btn-warning"
                                disabled={!suggestion}
                                type="button"
                                onClick={() => handleSubmit()}
                            >
                                {t('document.navbar.main.editor.left.blocks.modals.blockImprovementModal.btn.useText')}
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </>
        </FormModal>
    );

    function handleSubmit() {
        let newValue = suggestion.trim();

        if (!newValue.startsWith('<')) {
            newValue = `<p>${newValue}</p>`;
        }

        handleBlockChange(newValue);
        handleClose();
    }
}

function Settings({ document, option1, setOption1, option2, setOption2, isLoading }) {
    const { t } = useTranslation();

    const optionMapping = {
        a2: 'A2-niveau',
        b1: 'B1-niveau',
        b2: 'B2-niveau',
        c1: 'C1-niveau',
        c2: 'C2-niveau',
        je_naar_u: 'je/jij naar u',
        u_naar_je: 'u naar je/jij',
    };

    const optionMapping2 = {
        formal: t('documents:document.navbar.main.editor.left.blocks.modals.blockImprovementModal.formal'),
        informal: t('documents:document.navbar.main.editor.left.blocks.modals.blockImprovementModal.informal'),
    };

    const showOption2 = ['a2', 'b1', 'b2', 'c1', 'c2'].includes(option1);

    return (
        <div className="flex-center mb-3">
            <div>Ik wil een</div>
            <DropdownButton
                id="option1"
                title={optionMapping[option1] ?? 'Unknown option'}
                size="sm"
                variant="outline-primary"
                className="mx-2"
                onSelect={(eventKey) => setOption1(eventKey)}
            >
                <Dropdown.Item eventKey="a2" active={option1 === 'a2'}>
                    {optionMapping['a2']}
                </Dropdown.Item>
                <Dropdown.Item eventKey="b1" active={option1 === 'b1'}>
                    {optionMapping['b1']}
                </Dropdown.Item>
                <Dropdown.Item eventKey="b2" active={option1 === 'b2'}>
                    {optionMapping['b2']}
                </Dropdown.Item>
                <Dropdown.Item eventKey="c1" active={option1 === 'c1'}>
                    {optionMapping['c1']}
                </Dropdown.Item>
                <Dropdown.Item eventKey="c2" active={option1 === 'c2'}>
                    {optionMapping['c2']}
                </Dropdown.Item>

                {document.properties.ai.languageIso === 'nl' && (
                    <>
                        <Dropdown.Divider />
                        <Dropdown.Item eventKey="je_naar_u" active={option1 === 'je_naar_u'}>
                            {optionMapping['je_naar_u']}
                        </Dropdown.Item>
                        <Dropdown.Item eventKey="u_naar_je" active={option1 === 'u_naar_je'}>
                            {optionMapping['u_naar_je']}
                        </Dropdown.Item>
                    </>
                )}
            </DropdownButton>
            <div>tekst</div>

            {showOption2 && (
                <>
                    <div>&nbsp;in een</div>

                    <DropdownButton
                        id="option2"
                        title={optionMapping2[option2] ?? 'Unknown option'}
                        size="sm"
                        variant="outline-primary"
                        className="mx-2"
                        onSelect={(eventKey) => setOption2(eventKey)}
                    >
                        <Dropdown.Item eventKey="formal" active={option2 === 'formal'}>
                            {optionMapping2['formal']}
                        </Dropdown.Item>
                        <Dropdown.Item eventKey="informal" active={option2 === 'informal'}>
                            {optionMapping2['informal']}
                        </Dropdown.Item>
                    </DropdownButton>

                    <div>stijl</div>
                </>
            )}

            {isLoading && <Spinner className="ml-3" />}
        </div>
    );
}

function RenderedBody({ block, document, suggestion, setSuggestion }) {
    const dispatch = useDispatch();
    const currentOrganisation = useCurrentOrganisation();
    const { t } = useTranslation('documents');
    const { activeVariantId } = useContext(AreaContext);
    const { areaId } = useParams();
    const askUrl = getEnv('AI_API_BASE_URL') + '/ask';
    let isCalling = false;

    const [option1, setOption1] = useLocalStorage('document_ai_target_level', 'b1');
    const [option2, setOption2] = useLocalStorage('document_ai_target_style', 'formal');

    const [errorMessage, setErrorMessage] = useState('');
    const [opinion, setOpinion] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [isInitial, setIsInitial] = useState(true);

    useEffect(() => {
        if (isInitial && !isCalling) {
            isCalling = true;

            handleSubmit();
            setIsInitial(false);
        }
    }, []);

    useEffect(() => {
        if (isInitial) {
            return;
        }

        handleSubmit();
    }, [option1, option2, isInitial]);

    return (
        <Modal.Body>
            {errorMessage !== '' && <div className="text-danger">{errorMessage}</div>}

            <Settings
                document={document}
                option1={option1}
                setOption1={setOption1}
                option2={option2}
                setOption2={setOption2}
                isLoading={isLoading}
            />

            <div className="pt-3">
                {opinion !== '' && (
                    <>
                        <RenderOpinion opinion={opinion} />

                        {isLoading ? (
                            <Spinner />
                        ) : (
                            <div className="flex-center">
                                <PrimaryButton onClick={loadSuggestion} disabled={isLoading}>
                                    <Stars size={16} />
                                    {t(
                                        'document.navbar.main.editor.left.blocks.modals.blockImprovementModal.submitSuggestion',
                                        { level: option1.toUpperCase() },
                                    )}
                                </PrimaryButton>
                            </div>
                        )}
                    </>
                )}

                {suggestion !== '' && <RenderSuggestion suggestion={suggestion} block={block} isLoading={isLoading} />}

                {opinion !== '' || (suggestion !== '' && <Disclaimer />)}
            </div>
        </Modal.Body>
    );

    function handleSubmit() {
        if (isLoading) {
            return;
        }

        setLoading(true);
        setErrorMessage('');

        if (['a2', 'b1', 'b2', 'c1', 'c2'].includes(option1)) {
            getOpinion();
        } else {
            setOpinion('');
            loadSuggestion();
        }
    }

    async function getOpinion() {
        setOpinion('');
        setSuggestion('');

        let fullResult = '';

        try {
            let events = await streamEvents(askUrl, {
                entityType: 'block',
                entityId: `${block.id}`,
                prompt: `is_${option1}`,
                targetLevel: option1,
                languageIso: document.properties.ai.languageIso,
                properties: {
                    areaId: parseInt(areaId),
                    variantId: activeVariantId,
                },
            });

            for await (let event of events) {
                if (event.data.includes('[DONE]')) {
                    break;
                }

                if (!event.data.includes('choices')) continue;
                const chunk = JSON.parse(event.data);
                const choices = chunk.choices;

                if (choices.length === 0) continue;
                const delta = choices[0].delta;

                if (!delta) continue;
                const content = delta.content ?? '';

                if (content.length === 0) continue;

                fullResult += content;
                setOpinion((prev) => prev + content);
            }
        } catch (e) {
            setLoading(false);
            setOpinion('');
            setErrorMessage(t('document.navbar.main.editor.left.blocks.modals.blockImprovementModal.errorMessage'));
            throw e;
        } finally {
            // Invalidate cache
            dispatch(authApi.util.invalidateTags([{ type: 'Organisation', id: `${currentOrganisation.id}` }]));

            isCalling = false;
        }

        setLoading(false);
        setOpinion(fullResult);
    }

    async function loadSuggestion() {
        setSuggestion('');

        let fullResult = '';

        try {
            let events = await streamEvents(askUrl, {
                entityType: 'block',
                entityId: `${block.id}`,
                prompt: `suggest_${option1}_${option2}`,
                properties: {
                    areaId: parseInt(areaId),
                    variantId: activeVariantId,
                },
            });

            for await (let event of events) {
                if (event.data.includes('[DONE]')) {
                    break;
                }

                if (!event.data.includes('choices')) continue;
                const chunk = JSON.parse(event.data);
                const choices = chunk.choices;

                if (choices.length === 0) continue;
                const delta = choices[0].delta;

                if (!delta) continue;
                const content = delta.content ?? '';

                if (content.length === 0) continue;

                fullResult += content;
                setSuggestion((prev) => prev + content);
            }
        } catch (e) {
            setLoading(false);
            setSuggestion('');
            setErrorMessage(t('document.navbar.main.editor.left.blocks.modals.blockImprovementModal.errorMessage'));
            throw e;
        } finally {
            // Invalidate cache
            dispatch(authApi.util.invalidateTags([{ type: 'Organisation', id: `${currentOrganisation.id}` }]));
        }

        setSuggestion(fullResult);
        setLoading(false);
    }
}

function RenderOpinion({ opinion }) {
    return (
        <div className="mb-4">
            <Markdown rehypePlugins={[rehypeRaw]}>{opinion}</Markdown>
        </div>
    );
}

function RenderSuggestion({ suggestion, block, isLoading = true }) {
    const { t } = useTranslation();
    const [hideChanges, setHideChanges] = useState(false);

    return (
        <div className="pt-4">
            <div className="flex-center justify-content-between mb-2">
                <div className="font-weight-bold">
                    {t('documents:document.navbar.main.editor.left.blocks.modals.blockImprovementModal.suggestion')}:
                </div>

                <Switch
                    label={t('documents:document.navbar.main.editor.left.blocks.changes')}
                    name="show-suggestions-changes-switch"
                    checked={!hideChanges}
                    onChange={() => setHideChanges(!hideChanges)}
                />
            </div>

            <div className="area-read-only editor-right-side p-3 border-0 bg-light">
                {isLoading || hideChanges ? (
                    <Markdown rehypePlugins={[rehypeRaw]}>{suggestion}</Markdown>
                ) : (
                    <TextDiffRender value={block.latestContent} targetValue={suggestion} />
                )}
            </div>
        </div>
    );
}

function RateLimitExceededMessage({ handleClose }) {
    const { t } = useTranslation();

    return (
        <>
            <Modal.Body>
                <div className="text-danger">
                    {t('documents:document.navbar.main.editor.left.blocks.modals.blockImprovementModal.cannotUse')}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="btn btn-secondary" onClick={() => handleClose()}>
                    {t('global:btn.close')}
                </Button>
            </Modal.Footer>
        </>
    );
}

function Disclaimer() {
    const { t } = useTranslation();
    const currentOrganisation = useCurrentOrganisation();

    const hasRateLimit = currentOrganisation && currentOrganisation.aiRateLimit >= 0;

    return (
        <div className="text-secondary small mt-5">
            {t('documents:document.navbar.main.editor.left.blocks.modals.blockImprovementModal.generatedByAi')}
            {hasRateLimit && (
                <>
                    &nbsp;
                    {t('documents:document.navbar.main.editor.left.blocks.modals.blockImprovementModal.limitedChecks', {
                        count: currentOrganisation.aiRateLimit - currentOrganisation.aiUsageCount,
                    })}
                </>
            )}
        </div>
    );
}
